import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { RefreshActions } from '../state/refresh'
import { useRequests } from '../requests/useRequests'
import {
  validateUsername,
  validatePassword
} from '../utils/validators'
import '../styles/auth.css'

const LoginPage = (): JSX.Element => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const requestMaker = useRequests()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [usernameError, setUsernameError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [rememberMe, setRememberMe] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showVerificationMessage, setShowVerificationMessage] = useState(false)

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    if (searchParams.get('type') === 'new-account') {
      setShowVerificationMessage(true)
    }
  }, [location])

  const handleLogin = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setIsLoading(true)

    if (validateUsername(username, setUsernameError) &&
        validatePassword(password, setPasswordError)) {
      try {
        const tokenResponse = await requestMaker.getToken({
          username,
          password
        })

        if (rememberMe) {
          localStorage.setItem('Kreatlo-username', username)
          localStorage.setItem('Kreatlo-password', password)
        }

        localStorage.setItem('Kreatlo-jwtToken', tokenResponse.data.Authorization)
        dispatch(RefreshActions.setToastMessage({ message: 'Logowanie powiodło się' }))
        dispatch(RefreshActions.refreshUserState())
        navigate('/')
      } catch (e: any) {
        if (e.status === 401 && e.data?.message === 'ACCOUNT_NOT_ACTIVATED') {
          setPasswordError('Konto nie zostało aktywowane. Sprawdź swoją skrzynkę email i kliknij w link aktywacyjny.')
        } else {
          setPasswordError('Niepoprawne hasło')
        }
      }
    }

    setIsLoading(false)
  }

  const handleGoogleLogin = async (credentials: {
    credential: string
    select_by: string
    clientId: string
  }): Promise<void> => {
    try {
      const googleToken = credentials.credential
      const tokenResponse = await requestMaker.getToken({
        googleToken
      })
      localStorage.setItem('Kreatlo-googleToken', googleToken)
      localStorage.setItem('Kreatlo-jwtToken', tokenResponse.data.Authorization)
      dispatch(RefreshActions.setToastMessage({ message: 'Logowanie powiodło się' }))
      dispatch(RefreshActions.refreshUserState())
      navigate('/')
    } catch (e) {
      setPasswordError('Niepoprawne logowanie')
    }
  }

  return (
    <div className='login-page-wrapper'>
      <div className='login-left-panel'>
        <img src='/assets/images/logo/logo-black.png' alt='kreatlo logo' className='login-logo' />
        <h2>Już tylko jeden krok dzieli Cię od własnego konta w aplikacji Kreatlo</h2>
        <p>
          Korzystaj z wszystkich dostępnych funkcjonalności za darmo przez 30 dni!
        </p>
      </div>
      <div className='login-right-panel'>
        <div className='auth-container'>
          <h1 className='auth-title'>Logowanie</h1>
          {showVerificationMessage && (
            <div className='verification-message'>
              <p>Konto zostało utworzone! Sprawdź swoją skrzynkę email i kliknij w link aktywacyjny.</p>
              <p>Po aktywacji konta będziesz mógł się zalogować używając swojego adresu email i hasła.</p>
            </div>
          )}
          <p className='auth-description'>
            Zaloguj się, aby korzystać z pełni możliwości naszej platformy.
          </p>

          <form onSubmit={handleLogin} className='auth-form'>
            <div className='form-group'>
              <input
                type='text'
                className={`form-control ${usernameError ? 'error' : ''}`}
                placeholder='Login lub Email'
                value={username}
                onChange={(e) => {
                  setUsernameError(null)
                  setUsername(e.target.value)
                }}
                required
              />
              {usernameError && <div className='error-text'>{usernameError}</div>}
            </div>

            <div className='form-group'>
              <input
                type='password'
                className={`form-control ${passwordError ? 'error' : ''}`}
                placeholder='Hasło'
                value={password}
                onChange={(e) => {
                  setPasswordError(null)
                  setPassword(e.target.value)
                }}
                required
              />
              {passwordError && <div className='error-text'>{passwordError}</div>}
            </div>

            <div className='form-options'>
              {/* <div className="custom-checkbox">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                />
                <label htmlFor="rememberMe">Zapamiętaj mnie</label>
              </div> */}
              <button
                type='button'
                className='forgot-link'
                onClick={() => navigate('/forgot-password')}
              >
                Zapomniałeś hasła?
              </button>
            </div>

            <button
              type='submit'
              className='btn-primary'
              disabled={isLoading || !username || !password}
            >
              {isLoading ? 'Logowanie...' : 'Zaloguj'}
            </button>

            {/* <div className="google-login">
              <GoogleLogin
                onSuccess={handleGoogleLogin as any}
                onError={() => {
                  setPasswordError('Wystąpił błąd podczas logowania przez Google')
                }}
                auto_select
              />
            </div> */}
          </form>

          <div className='auth-footer'>
            <span className='footer-text'>Nie masz konta?</span>
            <button className='register-link' onClick={() => navigate('/register')}>
              Zarejestruj się <i className='fas fa-arrow-right' />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
